import React, { useMemo } from 'react';
import { 
  CheckCircle, 
  AlertTriangle, 
  XCircle, 
  TrendingDown, 
  TrendingUp,
  Activity,
  BarChart3,
  FileText,
  Shield,
  Target
} from 'lucide-react';
import { applySSOTOverride } from '@/utils/ssotOverride';
import { formatMissingFishRange } from '@/utils/reportFormatters';
import { useAuth } from '@/App';

/**
 * DecisionReportPdf - Clean, client-facing PDF-only layout
 * 
 * ★ ALL narrative text sourced ONLY from DecisionContext via applySSOTOverride
 * 
 * Structure:
 *   Page 1: Executive Summary
 *   Page 2: Key Charts (Growth + Feed Variance)
 *   Page 3: Missing Fish Intelligence (condensed)
 * 
 * Max 3-5 pages. No raw data, no tables, no UI controls.
 */

// Actionability badge colors — standardized 3-state system
const ACTIONABILITY_STYLES = {
  ACTIONABLE: {
    bg: 'bg-cyan-100',
    border: 'border-cyan-300',
    text: 'text-cyan-800',
    icon: CheckCircle,
    label: 'ACTIONABLE'
  },
  VALIDATION_REQUIRED: {
    bg: 'bg-red-100',
    border: 'border-red-300',
    text: 'text-red-800',
    icon: AlertTriangle,
    label: 'VALIDATION REQUIRED'
  },
  MONITOR_ONLY: {
    bg: 'bg-amber-100',
    border: 'border-amber-300',
    text: 'text-amber-800',
    icon: Activity,
    label: 'MONITOR ONLY'
  },
};

/**
 * Page 1: Executive Summary
 * ★ ALL narrative text sourced ONLY from DecisionContext via applySSOTOverride
 */
const ExecutiveSummaryPage = ({ 
  activeRecord, 
  ssot, 
  decisionContext, 
  sharedMetrics,
  dataQuality,
  // Live Model FCR from the parent — reflects the CURRENT FCR version
  // + multiplier selection. Preferred over `sharedMetrics.model_fcr`,
  // which is captured at the moment MFI last ran and can lag behind
  // the dropdown until the next re-run.
  liveModelFCR = null,
  // Version metadata resolved from the active FCR version selection.
  // Rendered as its own field, distinct from the manual Model
  // Adjustment (%) input.
  activeFcrVersion = null,
  modelAdjustmentPercent = null,
}) => {
  // ═══════════════════════════════════════════════════════════════════════════
  // SSOT OVERRIDE — Single enforcement point for narrative consistency
  // ═══════════════════════════════════════════════════════════════════════════
  const { user } = useAuth();
  const isAdminViewer = !!user?.is_admin;
  const overriddenOutput = useMemo(() => {
    return applySSOTOverride({
      detection: ssot?.conclusion?.type,
      interpretation: null,
      growthInsight: null,
      feeding: ssot?.feedingState,
      feedingExplanation: null,
    }, decisionContext, ssot);
  }, [ssot, decisionContext]);

  const conclusion = ssot?.conclusion;
  const financialConfidenceLevel = ssot?.financialConfidenceLevel || 'not_actionable';
  const actionability = conclusion?.actionability || decisionContext?.decisionClass || 'VALIDATION_REQUIRED';
  const style = ACTIONABILITY_STYLES[actionability] || ACTIONABILITY_STYLES.VALIDATION_REQUIRED;
  const ActionIcon = style.icon;

  // Key values — use overridden values for precision control
  const missingCount = overriddenOutput?.missingFishCount ?? conclusion?.missingFishCount ?? ssot?.missingFishCount ?? 0;
  const missingPercent = overriddenOutput?.missingFishPercent ?? conclusion?.missingFishPercent ?? ssot?.missingFishPercent ?? 0;
  const detectionStrength = conclusion?.detectionStrength ?? ssot?.detectionStrength ?? 'Low';
  const wastedFeedKg = conclusion?.wastedFeedKg ?? ssot?.wastedFeedKg ?? 0;
  const financialImpactEur = conclusion?.financialImpactEur ?? ssot?.financialImpactEur ?? 0;
  const reliabilityScore = conclusion?.dataReliabilityScore ?? dataQuality?.score ?? 50;

  // FCR values — prefer the LIVE model FCR (fetched with the current
  // FCR version + multiplier) and fall back to the saved MFI figure
  // only when the live value is absent.
  const reportedFCR = sharedMetrics?.reported_fcr;
  const modelFCR = (liveModelFCR != null) ? liveModelFCR : sharedMetrics?.model_fcr;
  const fcrGapPercent = sharedMetrics?.fcr_gap_percent;

  // ★ PRD canonical missing-fish range — rendered on every missing-fish PDF tile.
  const missingRange = formatMissingFishRange({
    missingFishCount: missingCount,
    dataReliabilityScore: reliabilityScore,
  });

  // Actions
  const primaryAction = decisionContext?.primaryAction;
  const secondaryActions = (decisionContext?.secondaryActions || []).slice(0, 2);
  const validation = decisionContext?.validation;

  // ★ Key finding — Use overriddenOutput.finalDetectionLabel (SSOT enforced)
  const keyFinding = overriddenOutput?.finalDetectionLabel || conclusion?.statement || 'Analysis complete — review details below.';

  return (
    <div 
      className="bg-white p-8 space-y-6 min-h-[1100px]" 
      data-pdf-section="executive-summary"
      style={{ pageBreakAfter: 'always' }}
    >
      {/* Header */}
      <div className="flex items-start justify-between border-b-2 border-gray-200 pb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analysis Report</h1>
          <p className="text-sm text-gray-500 mt-1">{activeRecord?.name} • {activeRecord?.species}</p>
          <p className="text-xs text-gray-400 mt-0.5">
            {activeRecord?.days_in_sea ? `${activeRecord.days_in_sea} days` : ''} 
            {activeRecord?.fish_count ? ` • ${activeRecord.fish_count.toLocaleString()} fish reported` : ''}
          </p>
        </div>
        <div className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${style.bg} ${style.border}`}>
          <ActionIcon className={`w-5 h-5 ${style.text}`} />
          <span className={`text-sm font-bold ${style.text}`}>{style.label}</span>
        </div>
      </div>

      {/* KEY FINDING - Hero section */}
      <div className={`rounded-xl p-6 ${
        conclusion?.hasConflict ? 'bg-gradient-to-r from-amber-800 to-amber-700' : 'bg-gradient-to-r from-slate-800 to-slate-700'
      } text-white`}>
        <div className="flex items-center gap-2 mb-2">
          <Target className="w-5 h-5 text-slate-300" />
          <span className="text-xs uppercase tracking-wider text-slate-300 font-medium">Key Finding</span>
        </div>
        <p className="text-xl font-bold leading-tight">{keyFinding}</p>
        {detectionStrength !== 'Low' && missingCount > 0 && !conclusion?.hasConflict && (
          <p className="text-sm text-slate-300 mt-2">
            {detectionStrength} indication • {missingRange.label} ({missingPercent}%)
          </p>
        )}
      </div>

      {/* FINANCIAL IMPACT */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
          <p className="text-xs uppercase tracking-wider text-gray-500 mb-2">Estimated Feed Waste</p>
          <p className={`text-2xl font-bold ${wastedFeedKg > 0 ? 'text-red-600' : 'text-green-600'}`}>
            {wastedFeedKg > 0 ? `${wastedFeedKg.toLocaleString()} kg` : 'None detected'}
          </p>
        </div>
        <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
          <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">Financial Impact</p>
          {financialConfidenceLevel === 'not_actionable' ? (
            <p className="text-sm text-gray-500 italic mt-2">Insufficient data for estimate</p>
          ) : financialConfidenceLevel === 'indicative' ? (
            <>
              <p className="text-[10px] text-amber-600 font-medium mb-1">Indicative — validation required</p>
              <p className="text-2xl font-bold text-amber-600">
                {financialImpactEur > 0 ? `~€${financialImpactEur.toLocaleString()}` : '€0'}
              </p>
            </>
          ) : (
            <p className={`text-2xl font-bold ${financialImpactEur > 0 ? 'text-red-600' : 'text-green-600'}`}>
              €{financialImpactEur.toLocaleString()}
            </p>
          )}
        </div>
      </div>

      {/* CORE EVIDENCE */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <BarChart3 className="w-4 h-4 text-gray-500" />
          <span className="text-xs uppercase tracking-wider text-gray-500 font-medium">Core Evidence</span>
        </div>
        <div className="grid grid-cols-5 gap-3">
          <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 text-center">
            <p className="text-[10px] text-gray-500 uppercase">Reported FCR</p>
            <p className="text-lg font-bold text-gray-800">{reportedFCR?.toFixed(2) || '—'}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 text-center">
            <p className="text-[10px] text-gray-500 uppercase">Model FCR</p>
            <p className="text-lg font-bold text-gray-800">{modelFCR?.toFixed(2) || '—'}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 text-center">
            <p className="text-[10px] text-gray-500 uppercase">FCR Gap</p>
            <p className={`text-lg font-bold ${fcrGapPercent > 10 ? 'text-red-600' : fcrGapPercent > 5 ? 'text-amber-600' : 'text-gray-800'}`}>
              {fcrGapPercent ? `${fcrGapPercent > 0 ? '+' : ''}${fcrGapPercent.toFixed(1)}%` : '—'}
            </p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 text-center">
            <p className="text-[10px] text-gray-500 uppercase">Missing Fish</p>
            <p className={`text-lg font-bold ${
              missingCount > 0 ? 'text-red-600' : isAdminViewer ? 'text-slate-700' : 'text-gray-800'
            }`}>
              {missingCount > 0
                ? missingRange.shortLabel
                : isAdminViewer ? <>10,000 <span className="text-[9px] font-medium text-slate-500">(baseline)</span></> : '—'}
            </p>
            {missingCount > 0 && !missingRange.isWithinMargin && (
              <p className="text-[9px] text-gray-400 mt-0.5">midpoint ~{missingRange.midpointLabel}</p>
            )}
            {missingCount === 0 && isAdminViewer && (
              <p className="text-[9px] text-slate-500 mt-0.5">Admin baseline</p>
            )}
          </div>
          <div className="bg-gray-50 rounded-lg p-3 border border-gray-200 text-center">
            <p className="text-[10px] text-gray-500 uppercase">Detection</p>
            <p className={`text-lg font-bold ${detectionStrength === 'High' ? 'text-red-600' : detectionStrength === 'Moderate' ? 'text-amber-600' : 'text-gray-800'}`}>
              {detectionStrength}
            </p>
          </div>
        </div>
      </div>

      {/* FCR MODEL CONFIGURATION — dedicated field for the active FCR
          version + its multiplier, rendered SEPARATE from the manual
          Model Adjustment (%) input so the two are never conflated. */}
      {(activeFcrVersion || (modelAdjustmentPercent != null && modelAdjustmentPercent !== 100)) && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3" data-pdf-fcr-version="true">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">FCR Model Configuration</span>
          </div>
          <div className="flex flex-wrap items-center gap-4 text-sm">
            {activeFcrVersion && (
              <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-wider text-slate-500">Version</span>
                <span className="font-semibold text-slate-800">
                  {activeFcrVersion.origin_name} — {activeFcrVersion.version_name}
                </span>
                <span
                  className={`px-2 py-0.5 rounded-md text-xs font-bold ${
                    Number(activeFcrVersion.multiplier_percent) === 100
                      ? 'bg-slate-200 text-slate-700'
                      : Number(activeFcrVersion.multiplier_percent) > 100
                        ? 'bg-red-100 text-red-700'
                        : 'bg-emerald-100 text-emerald-700'
                  }`}
                >
                  {activeFcrVersion.multiplier_percent}%
                </span>
              </div>
            )}
            {modelAdjustmentPercent != null && modelAdjustmentPercent !== 100 && (
              <div className="flex items-center gap-2 pl-4 border-l border-slate-300">
                <span className="text-[10px] uppercase tracking-wider text-amber-700">Manual Model Adjustment</span>
                <span className="font-semibold text-amber-800">
                  {modelAdjustmentPercent > 100 ? '+' : ''}{modelAdjustmentPercent - 100}%
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* DATA CONFIDENCE */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Shield className="w-4 h-4 text-gray-500" />
          <span className="text-xs uppercase tracking-wider text-gray-500 font-medium">Data Confidence</span>
        </div>
        <div className="flex items-center gap-4">
          <div className={`px-4 py-2 rounded-lg text-sm font-bold ${
            reliabilityScore >= 70 ? 'bg-green-100 text-green-800 border border-green-200' :
            reliabilityScore >= 50 ? 'bg-amber-100 text-amber-800 border border-amber-200' :
            'bg-red-100 text-red-800 border border-red-200'
          }`}>
            {/* ★ H2 — canonical confidence label, never 'Good/Acceptable/Low'. */}
            {ssot?.confidenceLabel || 'Uncertain'}
          </div>
          <span className="text-xs text-gray-500">Data reliability {reliabilityScore}/100</span>
          {financialConfidenceLevel !== 'confirmed' && ssot?.financialConfidenceReason && (
            <span className="text-xs text-gray-500 italic">{ssot.financialConfidenceReason}</span>
          )}
        </div>
        {/* Key drivers */}
        {decisionContext?.dataReliability?.drivers && (
          <div className="mt-2 flex flex-wrap gap-1">
            {decisionContext.dataReliability.drivers
              .filter(d => d.status !== 'good')
              .slice(0, 3)
              .map((d, i) => (
                <span key={i} className="text-[10px] px-2 py-0.5 rounded bg-gray-200 text-gray-600">
                  {d.detail}
                </span>
              ))}
          </div>
        )}
      </div>

      {/* ★ INTERPRETATION — Uses overriddenOutput (SSOT enforced) */}
      {(overriddenOutput?.finalInterpretation || overriddenOutput?.detectionExplanation || conclusion?.interpretation || conclusion?.detectionExplanation) && (
        <div className={`rounded-lg p-4 border ${
          overriddenOutput?.hasConflict || conclusion?.hasConflict ? 'bg-amber-50 border-amber-200' : 'bg-blue-50 border-blue-200'
        }`}>
          <p className="text-xs uppercase tracking-wider text-gray-500 font-medium mb-2">Interpretation</p>
          {/* ★ Use overriddenOutput.finalInterpretation (SSOT enforced) */}
          {(overriddenOutput?.finalInterpretation || conclusion?.interpretation) && (
            <p className={`text-sm ${overriddenOutput?.hasConflict || conclusion?.hasConflict ? 'text-amber-800' : 'text-blue-800'}`}>
              {overriddenOutput?.finalInterpretation || conclusion.interpretation}
            </p>
          )}
          {/* ★ DETECTION EXPLANATION — signal hierarchy clarity */}
          {(overriddenOutput?.detectionExplanation || conclusion?.detectionExplanation) && (
            <p className={`text-xs mt-2 pt-2 border-t ${
              overriddenOutput?.hasConflict || conclusion?.hasConflict ? 'text-amber-600 border-amber-200' : 'text-blue-600 border-blue-200'
            }`}>
              {overriddenOutput?.detectionExplanation || conclusion.detectionExplanation}
            </p>
          )}
          {/* ★ GROWTH INSIGHT — standardized jump status */}
          {(overriddenOutput?.finalGrowthInsight || conclusion?.growthInsight) && (
            <p className="text-xs text-gray-500 mt-1 italic">{overriddenOutput?.finalGrowthInsight || conclusion.growthInsight}</p>
          )}
        </div>
      )}

      {/* RECOMMENDED ACTIONS */}
      <div className="bg-cyan-50 border border-cyan-200 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-3">
          <FileText className="w-4 h-4 text-cyan-600" />
          <span className="text-xs uppercase tracking-wider text-cyan-700 font-medium">Recommended Actions</span>
        </div>
        <div className="space-y-2">
          {primaryAction && (
            <div className="flex items-start gap-2">
              <span className="w-2 h-2 rounded-full bg-cyan-600 mt-1.5 shrink-0"></span>
              <p className="text-sm text-cyan-900 font-medium">{primaryAction.text}</p>
            </div>
          )}
          {secondaryActions.map((action, i) => (
            <div key={i} className="flex items-start gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 mt-1.5 shrink-0"></span>
              <p className="text-sm text-cyan-700">{action.text}</p>
            </div>
          ))}
        </div>
        {decisionContext?.stockRecordPolicy && (
          <p className="text-xs text-gray-500 italic mt-3 pt-3 border-t border-cyan-200">
            {decisionContext.stockRecordPolicy.message}
          </p>
        )}
      </div>

      {/* VALIDATION REQUIRED */}
      {validation?.required && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-xs uppercase tracking-wider text-red-600 font-bold mb-1">Validation Required</p>
          <p className="text-sm text-red-700">
            Confirm via: {validation.examples?.join(' • ') || 'sample / camera / harvest count'}
          </p>
        </div>
      )}

      {/* Footer */}
      <div className="pt-4 border-t border-gray-200 flex justify-between items-center text-[9px] text-gray-400">
        <span>Detection: {detectionStrength} | Reliability: {reliabilityScore}/100 | Financial: {financialConfidenceLevel}</span>
        <span>Generated: {new Date().toISOString().slice(0, 16).replace('T', ' ')} UTC</span>
      </div>
    </div>
  );
};

/**
 * Page 2: Key Charts Section Header (charts rendered separately via html2canvas)
 */
const KeyChartsHeader = () => (
  <div 
    className="bg-white px-8 pt-8 pb-4" 
    data-pdf-section="charts-header"
  >
    <div className="flex items-center gap-2 border-b border-gray-200 pb-3">
      <TrendingUp className="w-5 h-5 text-gray-600" />
      <h2 className="text-xl font-bold text-gray-900">Performance Charts</h2>
    </div>
    <p className="text-sm text-gray-500 mt-2">
      Visual analysis of growth trajectory, feed variance, and biomass trends over the production cycle.
    </p>
  </div>
);

/**
 * Page 3: Condensed Missing Fish Intelligence
 */
const CondensedMfiPage = ({ 
  ssot, 
  methodResolution,
  fcrBasedDetection,
  trajectoryJump,
  decisionContext
}) => {
  const { user } = useAuth();
  const isAdminViewer = !!user?.is_admin;
  const conclusion = ssot?.conclusion;
  const detectionStrength = ssot?.detectionStrength || 'Low';
  const primaryMethod = ssot?.primaryMethod || 'fcr';
  const primaryMethodLabel = ssot?.primaryMethodLabel || (primaryMethod === 'jump' ? 'Jump-Based' : 'FCR-Based');
  const missingCount = ssot?.missingFishCount || 0;
  const missingPercent = ssot?.missingFishPercent || 0;
  const missingRange = formatMissingFishRange(ssot || {});

  // Detection methods summary
  const methods = [];
  
  if (fcrBasedDetection?.detected || methodResolution?.fcrAvailable) {
    methods.push({
      label: 'FCR-Based',
      detected: true,
      estimate: fcrBasedDetection?.estimatedMissingFish || null,
      isActive: primaryMethod === 'fcr',
      confidence: fcrBasedDetection?.confidenceLevel || 'Moderate'
    });
  }
  
  if (trajectoryJump?.jump_event_detected || trajectoryJump?.trajectory_jump_detected) {
    const confirmed = trajectoryJump.jump_event_confirmed || trajectoryJump.trajectory_jump_confirmed;
    methods.push({
      label: 'Jump-Based',
      detected: true,
      estimate: trajectoryJump.estimated_missing_fish || trajectoryJump.trajectory_based_missing_fish_estimate,
      isActive: primaryMethod === 'jump',
      confirmed,
      // ★ Use standardized jump status message from SSOT
      note: ssot?.jumpStatusMessage || (confirmed ? 'Confirmed by subsequent samples' : 'No sustained upward shift — detected jumps are not confirmed')
    });
  }

  // ★ Feeding state — use CORRECTED values from SSOT (no "Optimal" + waste coexistence)
  const feedingState = ssot?.feedingState;
  const feedingNote = ssot?.feedingNote;
  const rfi = ssot?.rfi;
  const wastedFeedKg = ssot?.wastedFeedKg || 0;

  return (
    <div 
      className="bg-white p-8 space-y-5 min-h-[1100px]" 
      data-pdf-section="mfi-condensed"
      style={{ pageBreakAfter: 'always' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-200 pb-3">
        <div className="flex items-center gap-2">
          <Activity className="w-5 h-5 text-purple-600" />
          <h2 className="text-xl font-bold text-gray-900">Missing Fish Intelligence</h2>
        </div>
        <span className={`px-3 py-1 rounded-full text-xs font-bold ${
          primaryMethod === 'jump' ? 'bg-amber-100 text-amber-800' : 'bg-purple-100 text-purple-800'
        }`}>
          {primaryMethodLabel}
        </span>
      </div>

      {/* Detection Summary */}
      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-5">
        <div className="grid grid-cols-4 gap-4 text-center">
          <div>
            <p className="text-[10px] uppercase text-gray-500 mb-1">Detection Strength</p>
            <p className={`text-xl font-bold ${
              detectionStrength === 'High' ? 'text-red-600' : 
              detectionStrength === 'Moderate' ? 'text-amber-600' : 
              'text-gray-600'
            }`}>{detectionStrength}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase text-gray-500 mb-1">Est. Missing Fish</p>
            <p className={`text-xl font-bold ${
              missingCount > 0 ? 'text-red-600' : isAdminViewer ? 'text-slate-700' : 'text-gray-800'
            }`}>
              {missingCount > 0
                ? missingRange.shortLabel
                : isAdminViewer ? <>10,000 <span className="text-[9px] font-medium text-slate-500">(baseline)</span></> : 'None'}
            </p>
            {missingCount > 0 && !missingRange.isWithinMargin && (
              <p className="text-[9px] text-gray-400 mt-0.5">midpoint ~{missingRange.midpointLabel}</p>
            )}
            {missingCount === 0 && isAdminViewer && (
              <p className="text-[9px] text-slate-500 mt-0.5">Admin baseline</p>
            )}
          </div>
          <div>
            <p className="text-[10px] uppercase text-gray-500 mb-1">Deviation %</p>
            <p className={`text-xl font-bold ${missingPercent > 10 ? 'text-red-600' : missingPercent > 5 ? 'text-amber-600' : 'text-gray-800'}`}>
              {missingPercent > 0 ? `${missingPercent.toFixed(1)}%` : '—'}
            </p>
          </div>
          <div>
            <p className="text-[10px] uppercase text-gray-500 mb-1">Primary Method</p>
            <p className="text-xl font-bold text-purple-700">{primaryMethodLabel}</p>
          </div>
        </div>
      </div>

      {/* Methods Comparison */}
      {methods.length > 0 && (
        <div>
          <p className="text-xs uppercase tracking-wider text-gray-500 font-medium mb-3">Detection Method Results</p>
          <div className="grid grid-cols-1 gap-3">
            {methods.map((m, i) => (
              <div 
                key={i} 
                className={`rounded-lg border p-4 ${
                  m.isActive ? 'bg-purple-50 border-purple-300' : 'bg-gray-50 border-gray-200'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`font-semibold ${m.isActive ? 'text-purple-800' : 'text-gray-700'}`}>
                      {m.label}
                    </span>
                    {m.isActive && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-200 text-purple-800">PRIMARY</span>
                    )}
                  </div>
                  <span className={`text-sm font-bold ${m.estimate > 0 ? 'text-red-600' : 'text-gray-600'}`}>
                    {m.estimate ? `${m.estimate.toLocaleString()} fish` : 'N/A'}
                  </span>
                </div>
                {m.note && (
                  <p className="text-xs text-gray-500 mt-1 italic">{m.note}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Feeding Impact — ★ Uses corrected feedingState from SSOT */}
      {(feedingState || wastedFeedKg > 0) && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <p className="text-xs uppercase tracking-wider text-amber-700 font-medium mb-2">Feeding Impact Assessment</p>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-[10px] text-gray-500 uppercase">Feeding State</p>
              <p className={`text-lg font-bold ${
                feedingState === 'Overfeeding' ? 'text-red-600' : 
                feedingState === 'Restricted' ? 'text-blue-600' :
                feedingState === 'Mixed' ? 'text-amber-600' :
                'text-green-600'
              }`}>{feedingState || '—'}</p>
            </div>
            <div>
              <p className="text-[10px] text-gray-500 uppercase">Relative Feed Index</p>
              <p className="text-lg font-bold text-gray-800">{rfi != null ? `${rfi}%` : '—'}</p>
            </div>
            <div>
              <p className="text-[10px] text-gray-500 uppercase">Feed Wasted</p>
              <p className={`text-lg font-bold ${wastedFeedKg > 0 ? 'text-red-600' : 'text-green-600'}`}>
                {wastedFeedKg > 0 ? `${wastedFeedKg.toLocaleString()} kg` : 'None'}
              </p>
            </div>
          </div>
          {/* ★ Use feedingNote for corrected messaging (no "Optimal" + waste) */}
          {feedingNote && (
            <p className="text-xs text-amber-700 mt-3 pt-2 border-t border-amber-200">{feedingNote}</p>
          )}
          {!feedingNote && ssot?.feedingImpact && (
            <p className="text-xs text-amber-700 mt-3 pt-2 border-t border-amber-200">{ssot.feedingImpact}</p>
          )}
        </div>
      )}

      {/* Interpretation — ★ Uses canonical messages from SSOT */}
      {(conclusion?.statement || conclusion?.interpretation) && (
        <div className={`rounded-lg p-4 ${
          conclusion?.hasConflict ? 'bg-amber-50 border border-amber-200' : 'bg-blue-50 border border-blue-200'
        }`}>
          <p className="text-xs uppercase tracking-wider text-gray-500 font-medium mb-2">Interpretation</p>
          <p className={`text-sm ${conclusion?.hasConflict ? 'text-amber-800' : 'text-blue-800'}`}>
            {conclusion.interpretation || conclusion.statement}
          </p>
          {/* ★ DETECTION EXPLANATION — signal hierarchy clarity */}
          {conclusion?.detectionExplanation && (
            <p className={`text-xs mt-2 pt-2 border-t ${
              conclusion?.hasConflict ? 'text-amber-600 border-amber-200' : 'text-blue-600 border-blue-200'
            }`}>
              {conclusion.detectionExplanation}
            </p>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="pt-4 border-t border-gray-200 text-[9px] text-gray-400 text-center">
        <p>This report is for planning and operational guidance only. Do not modify stock records based on analytical results alone.</p>
      </div>
    </div>
  );
};

/**
 * Main PDF Export Component
 * Renders all pages for PDF capture
 */
const DecisionReportPdf = ({ 
  activeRecord, 
  missingFishIntelligence, 
  economicParams, 
  dataQuality,
  chartRefs, // Optional refs to charts for separate capture
  // Live Model FCR (from the parent's live FCR-version-aware fetch).
  // Preferred over `sharedMetrics.model_fcr` inside the report.
  liveModelFCR = null,
  // Resolved active FCR version — rendered as its own report field.
  activeFcrVersion = null,
  modelAdjustmentPercent = null,
}) => {
  const ssot = missingFishIntelligence?.ssot;
  const decisionContext = missingFishIntelligence?.decisionContext;
  const sharedMetrics = missingFishIntelligence?.sharedMetrics;
  const methodResolution = missingFishIntelligence?.methodResolution;
  const fcrBasedDetection = missingFishIntelligence?.fcrBasedDetection;
  const trajectoryJump = missingFishIntelligence?.trajectoryJump;

  return (
    <div className="decision-report-pdf bg-white" data-testid="decision-report-pdf">
      {/* Page 1: Executive Summary */}
      <ExecutiveSummaryPage
        activeRecord={activeRecord}
        ssot={ssot}
        decisionContext={decisionContext}
        sharedMetrics={sharedMetrics}
        dataQuality={dataQuality}
        liveModelFCR={liveModelFCR}
        activeFcrVersion={activeFcrVersion}
        modelAdjustmentPercent={modelAdjustmentPercent}
      />

      {/* Page 2 Header: Key Charts (charts rendered separately) */}
      <KeyChartsHeader />

      {/* Page 3: Condensed MFI */}
      <CondensedMfiPage
        ssot={ssot}
        methodResolution={methodResolution}
        fcrBasedDetection={fcrBasedDetection}
        trajectoryJump={trajectoryJump}
        decisionContext={decisionContext}
      />
    </div>
  );
};

export default DecisionReportPdf;
export { ExecutiveSummaryPage, KeyChartsHeader, CondensedMfiPage };
